/* =========================================================
   Supremo Surgical — Shopify Theme JS
   Cart integration via Shopify Ajax API + nav/header behavior
   ========================================================= */

// --- Cart (Shopify Ajax API) ---
const Cart = {
  async fetch() {
    const res = await fetch('/cart.js');
    return res.json();
  },
  async add(variantId, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    });
    const data = await res.json();
    if (res.ok) {
      await Cart.refresh();
      Cart.showToast(data.items?.[0]?.product_title || data.product_title || 'Added to cart');
    } else {
      console.warn('Cart add failed', data);
      Cart.showToast(data.description || 'Could not add item', true);
    }
    return data;
  },
  async change(line, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity: qty })
    });
    const data = await res.json();
    Cart.updateBadges(data.item_count || 0);
    return data;
  },
  async refresh() {
    const data = await Cart.fetch();
    Cart.updateBadges(data.item_count || 0);
    return data;
  },
  updateBadges(count) {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  },
  showToast(name, isError) {
    let toast = document.getElementById('ss-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'ss-toast';
      toast.style.cssText = `position:fixed;bottom:28px;right:28px;z-index:9999;
        background:#002e4a;color:#fff;padding:14px 20px;border-radius:10px;
        font-family:'Satoshi',sans-serif;font-size:14px;font-weight:700;
        box-shadow:0 18px 40px rgba(0,40,80,.25);
        transform:translateY(20px);opacity:0;transition:all 320ms cubic-bezier(0.2,0.6,0.2,1);
        display:flex;align-items:center;gap:10px;max-width:320px;`;
      document.body.appendChild(toast);
    }
    const color = isError ? '#b83a2d' : '#7aadcb';
    toast.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg><span>${isError ? '' : 'Added to cart: '}${name.length > 28 ? name.slice(0, 28) + '…' : name}</span>`;
    requestAnimationFrame(() => { toast.style.transform = 'translateY(0)'; toast.style.opacity = '1'; });
    clearTimeout(toast._t);
    toast._t = setTimeout(() => { toast.style.transform = 'translateY(20px)'; toast.style.opacity = '0'; }, 2800);
  }
};

// --- Sticky Header ---
function initStickyHeader() {
  const h = document.querySelector('.site-header');
  if (!h) return;
  const onScroll = () => h.classList.toggle('scrolled', window.scrollY > 20);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// --- Mobile Nav ---
function initMobileNav() {
  const toggle = document.querySelector('.mobile-nav-toggle');
  const nav = document.querySelector('.mobile-nav-panel');
  if (!toggle || !nav) return;
  toggle.addEventListener('click', () => {
    const open = nav.classList.toggle('open');
    toggle.setAttribute('aria-expanded', open);
    document.body.style.overflow = open ? 'hidden' : '';
  });
  nav.querySelectorAll('a').forEach(a => a.addEventListener('click', () => {
    nav.classList.remove('open');
    document.body.style.overflow = '';
  }));
}

// --- Product card add-to-cart forms (Shopify) ---
function initProductForms() {
  document.querySelectorAll('form[data-pcard-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const id = form.querySelector('[name="id"]')?.value;
      if (!id) return;
      const btn = form.querySelector('button[type="submit"]');
      const orig = btn?.innerHTML;
      if (btn) btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>';
      try { await Cart.add(id, 1); } catch (err) { console.error(err); }
      if (btn) setTimeout(() => { btn.innerHTML = orig; }, 1400);
    });
  });
}

// --- PDP variant + qty form ---
function initPdpForm() {
  const form = document.querySelector('form[data-pdp-form]');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = form.querySelector('[name="id"]')?.value;
    const qty = parseInt(form.querySelector('[name="quantity"]')?.value) || 1;
    if (!id) return;
    await Cart.add(id, qty);
  });

  // qty +/- buttons
  form.querySelectorAll('[data-qty-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = form.querySelector('[name="quantity"]');
      const cur = parseInt(input.value) || 1;
      const delta = parseInt(btn.dataset.qtyBtn);
      input.value = Math.max(1, cur + delta);
    });
  });

  // Variant chip selection
  const variantInput = form.querySelector('[name="id"]');
  form.querySelectorAll('[data-variant]').forEach(btn => {
    btn.addEventListener('click', () => {
      form.querySelectorAll('[data-variant]').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      if (btn.dataset.variant && variantInput) variantInput.value = btn.dataset.variant;
    });
  });
}

// --- PDP tab switcher ---
function initPdpTabs() {
  document.querySelectorAll('[data-pdp-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.pdpTab;
      document.querySelectorAll('[data-pdp-tab]').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('[data-pdp-pane]').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.querySelector(`[data-pdp-pane="${target}"]`)?.classList.add('active');
    });
  });

  // Thumb gallery
  document.querySelectorAll('[data-pdp-thumb]').forEach(t => t.addEventListener('click', () => {
    document.querySelectorAll('[data-pdp-thumb]').forEach(x => x.classList.remove('active'));
    t.classList.add('active');
    const main = document.querySelector('[data-pdp-main-img]');
    if (main && t.dataset.src) {
      const img = main.querySelector('img');
      if (img) img.src = t.dataset.src;
      else main.innerHTML = `<img src="${t.dataset.src}" alt="">`;
    }
  }));

  // Generic chip groups (finish, case)
  document.querySelectorAll('.pdp-opt-chips').forEach(group => {
    group.addEventListener('click', e => {
      if (e.target.tagName === 'BUTTON') {
        group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
        e.target.classList.add('active');
      }
    });
  });
}

// --- FAQ accordion ---
function initFAQ() {
  document.querySelectorAll('[data-faq]').forEach(item => {
    item.addEventListener('click', () => item.classList.toggle('open'));
  });
}

// --- Contact tabs ---
function initContactTabs() {
  document.querySelectorAll('[data-contact-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.contactTab;
      document.querySelectorAll('[data-contact-tab]').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('[data-contact-pane]').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.querySelector(`[data-contact-pane="${target}"]`)?.classList.add('active');
    });
  });
}

// --- Init ---
document.addEventListener('DOMContentLoaded', () => {
  Cart.refresh().catch(() => {});
  initStickyHeader();
  initMobileNav();
  initProductForms();
  initPdpForm();
  initPdpTabs();
  initFAQ();
  initContactTabs();
});
